import { PDFDocument, rgb } from 'pdf-lib';
import { saveAs } from 'file-saver';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import JSZip from 'jszip';
import { getToken } from './auth';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfs/pdf.worker.min.mjs';

export const downloadAnnotatedCopy = async (copy, pdfUrl) => {
  try {
    if (!copy || !pdfUrl) {
      throw new Error('Copy or PDF URL missing');
    }

    const student = copy.student;
    const exam = copy.questionPaper;
    const marksAwarded = copy.pages.reduce((sum, p) => sum + (p.marksAwarded || 0), 0);
    const completionDate = copy.evaluationCompletedAt
      ? new Date(copy.evaluationCompletedAt).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : 'Not completed';

    // Fetch PDF with Bearer token in Authorization header
    const token = getToken();
    const pdfResponse = await fetch(pdfUrl, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!pdfResponse.ok) throw new Error(`PDF fetch failed: ${pdfResponse.status}`);
    const pdfArrayBuffer = await pdfResponse.arrayBuffer();

    // Load the original PDF
    const pdfDoc = await pdfjsLib.getDocument({ data: pdfArrayBuffer }).promise;
    const pageCount = pdfDoc.numPages;

    // Create canvas for rendering PDF pages
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Get first page to determine dimensions
    const firstPage = await pdfDoc.getPage(1);
    const viewport = firstPage.getViewport({ scale: 2 });
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const A4_WIDTH = 210;
    const A4_HEIGHT = 297;
    const DPI = 96;
    const SCALE = (A4_WIDTH * DPI) / 25.4 / viewport.width;

    // Create new PDF document
    const newPdf = await PDFDocument.create();

    // ========== ADD SUMMARY PAGE ==========
    const summaryPage = newPdf.addPage([A4_WIDTH * 2.834645669, A4_HEIGHT * 2.834645669]);
    const { height, width } = summaryPage.getSize();
    const margin = 40;
    let yPos = height - margin;

    // Header
    summaryPage.drawText('COPY EVALUATION SUMMARY', {
      x: margin,
      y: yPos,
      size: 24,
      color: rgb(0, 0, 0),
      font: await newPdf.embedFont(PDFDocument.builtinFonts.HelveticaBold),
    });
    yPos -= 40;

    summaryPage.drawLine({
      start: { x: margin, y: yPos },
      end: { x: width - margin, y: yPos },
      thickness: 2,
      color: rgb(0, 0, 0),
    });
    yPos -= 30;

    // Student Info
    summaryPage.drawText('Student Information', {
      x: margin,
      y: yPos,
      size: 14,
      color: rgb(0, 0, 0),
      font: await newPdf.embedFont(PDFDocument.builtinFonts.HelveticaBold),
    });
    yPos -= 20;

    summaryPage.drawText(`Name: ${student?.name || 'N/A'}`, {
      x: margin + 20,
      y: yPos,
      size: 11,
      color: rgb(0, 0, 0),
    });
    yPos -= 16;

    summaryPage.drawText(`Email: ${student?.email || 'N/A'}`, {
      x: margin + 20,
      y: yPos,
      size: 11,
      color: rgb(0, 0, 0),
    });
    yPos -= 30;

    // Exam Info
    summaryPage.drawText('Exam Information', {
      x: margin,
      y: yPos,
      size: 14,
      color: rgb(0, 0, 0),
      font: await newPdf.embedFont(PDFDocument.builtinFonts.HelveticaBold),
    });
    yPos -= 20;

    summaryPage.drawText(`Subject: ${exam?.title || 'N/A'}`, {
      x: margin + 20,
      y: yPos,
      size: 11,
      color: rgb(0, 0, 0),
    });
    yPos -= 16;

    summaryPage.drawText(`Course: ${exam?.course || 'N/A'}`, {
      x: margin + 20,
      y: yPos,
      size: 11,
      color: rgb(0, 0, 0),
    });
    yPos -= 16;

    summaryPage.drawText(`Total Pages: ${copy.totalPages}`, {
      x: margin + 20,
      y: yPos,
      size: 11,
      color: rgb(0, 0, 0),
    });
    yPos -= 30;

    // Marks Info
    summaryPage.drawText('Marks Evaluation', {
      x: margin,
      y: yPos,
      size: 14,
      color: rgb(0, 0, 0),
      font: await newPdf.embedFont(PDFDocument.builtinFonts.HelveticaBold),
    });
    yPos -= 20;

    summaryPage.drawText(`Total Marks Awarded: ${marksAwarded}/${exam?.totalMarks || 'N/A'}`, {
      x: margin + 20,
      y: yPos,
      size: 12,
      color: rgb(40, 140, 0),
      font: await newPdf.embedFont(PDFDocument.builtinFonts.HelveticaBold),
    });
    yPos -= 20;

    // Page-wise breakdown
    summaryPage.drawText('Page-wise Marks:', {
      x: margin + 20,
      y: yPos,
      size: 11,
      color: rgb(0, 0, 0),
      font: await newPdf.embedFont(PDFDocument.builtinFonts.HelveticaBold),
    });
    yPos -= 16;

    copy.pages.slice(0, 10).forEach(page => {
      if (page.marksAwarded > 0 || page.pageNumber <= 5) {
        summaryPage.drawText(
          `  Page ${page.pageNumber}: ${page.marksAwarded || 0} marks`,
          {
            x: margin + 30,
            y: yPos,
            size: 10,
            color: rgb(0, 0, 0),
          }
        );
        yPos -= 14;
      }
    });

    yPos -= 20;

    // Examiner & Date
    summaryPage.drawText('Evaluation Details', {
      x: margin,
      y: yPos,
      size: 14,
      color: rgb(0, 0, 0),
      font: await newPdf.embedFont(PDFDocument.builtinFonts.HelveticaBold),
    });
    yPos -= 20;

    summaryPage.drawText(
      `Examiner: ${copy.examiners?.[0]?.name || copy.examiners?.[0] || 'N/A'}`,
      {
        x: margin + 20,
        y: yPos,
        size: 11,
        color: rgb(0, 0, 0),
      }
    );
    yPos -= 16;

    summaryPage.drawText(`Evaluation Date: ${completionDate}`, {
      x: margin + 20,
      y: yPos,
      size: 11,
      color: rgb(0, 0, 0),
    });

    // ========== ADD COPY PAGES ==========
    for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale: SCALE });

      // Render PDF page to canvas
      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;

      // Convert canvas to image
      const imageData = canvas.toDataURL('image/png');
      const pdfPage = newPdf.addPage([viewport.width, viewport.height]);

      // Embed image in PDF
      const image = await newPdf.embedPng(imageData);
      pdfPage.drawImage(image, {
        x: 0,
        y: 0,
        width: viewport.width,
        height: viewport.height,
      });
    }

    // Save PDF
    const pdfBytes = await newPdf.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    saveAs(blob, `${student?.name || 'copy'}_${exam?.title || 'exam'}_annotated.pdf`);

    return true;
  } catch (error) {
    console.error('Error downloading annotated copy:', error);
    throw error;
  }
};

export const downloadMultipleAnnotatedCopies = async (copies, pdfUrlMap, onProgress) => {
  try {
    if (!copies || copies.length === 0) {
      throw new Error('No copies selected');
    }

    const zip = new JSZip();
    let examName = 'copies';
    let completed = 0;

    for (const copy of copies) {
      const pdfUrl = pdfUrlMap[copy._id];
      if (!pdfUrl) {
        console.warn(`PDF URL not found for copy ${copy._id}, skipping...`);
        continue;
      }

      try {
        const student = copy.student;
        const exam = copy.questionPaper;
        if (exam?.title) {
          examName = exam.title;
        }
        const marksAwarded = copy.pages.reduce((sum, p) => sum + (p.marksAwarded || 0), 0);
        const completionDate = copy.evaluationCompletedAt
          ? new Date(copy.evaluationCompletedAt).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })
          : 'Not completed';

        // Fetch the original PDF with credentials
        const pdfResponse = await fetch(pdfUrl, { credentials: 'include' });
        if (!pdfResponse.ok) {
          throw new Error(`Failed to fetch PDF: ${pdfResponse.status}`);
        }
        const pdfArrayBuffer = await pdfResponse.arrayBuffer();
        const pdfDoc = await pdfjsLib.getDocument({ data: pdfArrayBuffer }).promise;
        const pageCount = pdfDoc.numPages;

        // Create canvas
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        const firstPage = await pdfDoc.getPage(1);
        const viewport = firstPage.getViewport({ scale: 2 });
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const A4_WIDTH = 210;
        const A4_HEIGHT = 297;
        const SCALE = (A4_WIDTH * 96) / 25.4 / viewport.width;

        // Create new PDF
        const newPdf = await PDFDocument.create();

        // Add summary page
        const summaryPage = newPdf.addPage([A4_WIDTH * 2.834645669, A4_HEIGHT * 2.834645669]);
        const { height, width } = summaryPage.getSize();
        const margin = 40;
        let yPos = height - margin;

        summaryPage.drawText('COPY EVALUATION SUMMARY', {
          x: margin,
          y: yPos,
          size: 24,
          color: rgb(0, 0, 0),
          font: await newPdf.embedFont(PDFDocument.builtinFonts.HelveticaBold),
        });
        yPos -= 40;

        summaryPage.drawLine({
          start: { x: margin, y: yPos },
          end: { x: width - margin, y: yPos },
          thickness: 2,
          color: rgb(0, 0, 0),
        });
        yPos -= 30;

        // Student Info
        summaryPage.drawText('Student Information', {
          x: margin,
          y: yPos,
          size: 14,
          color: rgb(0, 0, 0),
          font: await newPdf.embedFont(PDFDocument.builtinFonts.HelveticaBold),
        });
        yPos -= 20;
        summaryPage.drawText(`Name: ${student?.name || 'N/A'}`, {
          x: margin + 20,
          y: yPos,
          size: 11,
          color: rgb(0, 0, 0),
        });
        yPos -= 16;
        summaryPage.drawText(`Email: ${student?.email || 'N/A'}`, {
          x: margin + 20,
          y: yPos,
          size: 11,
          color: rgb(0, 0, 0),
        });
        yPos -= 30;

        // Exam Info
        summaryPage.drawText('Exam Information', {
          x: margin,
          y: yPos,
          size: 14,
          color: rgb(0, 0, 0),
          font: await newPdf.embedFont(PDFDocument.builtinFonts.HelveticaBold),
        });
        yPos -= 20;
        summaryPage.drawText(`Subject: ${exam?.title || 'N/A'}`, {
          x: margin + 20,
          y: yPos,
          size: 11,
          color: rgb(0, 0, 0),
        });
        yPos -= 16;
        summaryPage.drawText(`Total Marks: ${marksAwarded}/${exam?.totalMarks || 'N/A'}`, {
          x: margin + 20,
          y: yPos,
          size: 12,
          color: rgb(40, 140, 0),
          font: await newPdf.embedFont(PDFDocument.builtinFonts.HelveticaBold),
        });
        yPos -= 20;
        summaryPage.drawText(`Evaluation Date: ${completionDate}`, {
          x: margin + 20,
          y: yPos,
          size: 11,
          color: rgb(0, 0, 0),
        });

        // Add copy pages
        for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
          const page = await pdfDoc.getPage(pageNum);
          const vp = page.getViewport({ scale: SCALE });
          const renderContext = { canvasContext: context, viewport: vp };
          await page.render(renderContext).promise;

          const imageData = canvas.toDataURL('image/png');
          const pdfPage = newPdf.addPage([vp.width, vp.height]);
          const image = await newPdf.embedPng(imageData);
          pdfPage.drawImage(image, { x: 0, y: 0, width: vp.width, height: vp.height });
        }

        // Add to zip
        const pdfBytes = await newPdf.save();
        const fileName = `${student?.name || 'copy'}_${exam?.title || 'exam'}.pdf`;
        zip.file(fileName, pdfBytes);
        console.log(`✅ Added ${fileName} to zip`);

        completed++;
        if (onProgress) {
          onProgress(completed, copies.length);
        }
      } catch (err) {
        console.error(`Error processing copy ${copy._id}:`, err);
        throw err;
      }
    }

    console.log(`📦 Generating ZIP with ${completed} PDFs...`);

    // Generate and save ZIP
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const zipFileName = `${examName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_copies.zip`;
    console.log(`💾 Saving ZIP as: ${zipFileName}`);
    saveAs(zipBlob, zipFileName);

    return true;
  } catch (error) {
    console.error('Error downloading multiple copies:', error);
    throw error;
  }
};
